import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchHistory, SearchHistoryItem } from '../../context/SearchHistoryContext';
import {
  ArrowTopRightOnSquareIcon,
  BookmarkIcon as BookmarkOutlineIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { Eyebrow, PageHeading } from '../../design-system';
import styles from './SearchHistoryPage.module.css';

const ALL_HISTORY = 'All history';

const bucketPriority: Record<string, number> = {
  [ALL_HISTORY]: 0,
  Today: 1,
  Yesterday: 2,
  'Last 7 days': 3,
};

const getBucketKey = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();

  const isSameDay = (first: Date, second: Date) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();

  if (isSameDay(date, now)) {
    return 'Today';
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  }

  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays < 7) {
    return 'Last 7 days';
  }

  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
};

const buildQueryUrl = (query: string, path: string): string => {
  const encoded = encodeURIComponent(query);
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}?q=${encoded}`;
  }
  return `${path}?q=${encoded}`;
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

type DateFilterOption = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';

const SearchHistoryPage: React.FC = () => {
  const {
    searchHistory,
    removeFromSearchHistory,
    clearSearchHistory,
    toggleBookmark,
    bookmarks,
  } = useSearchHistory();

  const navigate = useNavigate();
  const location = useLocation();

  const [activeCollection, setActiveCollection] = useState<'history' | 'bookmarks'>('history');
  const [activeBucket, setActiveBucket] = useState<string>(ALL_HISTORY);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<SearchHistoryItem | null>(null);
  const [dateFilterOption, setDateFilterOption] = useState<DateFilterOption>('all');
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });
  const searchBasePath = location.pathname.startsWith('/admin/dashboard')
    ? '/admin/dashboard/search'
    : location.pathname.startsWith('/enterprise/dashboard')
      ? '/enterprise/dashboard/search'
      : location.pathname.startsWith('/startup/dashboard')
        ? '/startup/dashboard/search'
        : location.pathname.startsWith('/government/dashboard')
          ? '/government/dashboard/search'
          : '/search';

  const buildSearchResultsPath = (query: string) => {
    const encodedQuery = encodeURIComponent(query);
    return searchBasePath === '/search'
      ? `/search/results?q=${encodedQuery}`
      : `${searchBasePath}?q=${encodedQuery}`;
  };

  const historyBuckets = useMemo(() => {
    const initialBuckets = new Map<string, { count: number; sortValue: number }>([
      [ALL_HISTORY, { count: searchHistory.length, sortValue: bucketPriority[ALL_HISTORY] }],
      ['Today', { count: 0, sortValue: bucketPriority.Today }],
      ['Yesterday', { count: 0, sortValue: bucketPriority.Yesterday }],
      ['Last 7 days', { count: 0, sortValue: bucketPriority['Last 7 days'] }],
    ]);

    searchHistory.forEach((item) => {
      const bucketKey = getBucketKey(item.timestamp);
      const date = new Date(item.timestamp);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
      const sortValue =
        bucketPriority[bucketKey] ?? Number.MAX_SAFE_INTEGER - monthStart;

      const bucket = initialBuckets.get(bucketKey);
      if (bucket) {
        bucket.count += 1;
      } else {
        initialBuckets.set(bucketKey, { count: 1, sortValue });
      }
    });

    return Array.from(initialBuckets.entries())
      .sort((a, b) => a[1].sortValue - b[1].sortValue)
      .map(([label, data]) => ({ label, count: data.count }));
  }, [searchHistory]);

  const filteredByCollection = useMemo(() => {
    if (activeCollection === 'bookmarks') {
      return bookmarks;
    }

    if (activeBucket === ALL_HISTORY) {
      return searchHistory;
    }

    return searchHistory.filter(
      (item) => getBucketKey(item.timestamp) === activeBucket,
    );
  }, [activeCollection, activeBucket, searchHistory, bookmarks]);

  const dateRange = useMemo(() => {
    const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endOfDay = (date: Date) => {
      const end = new Date(date);
      end.setHours(23, 59, 59, 999);
      return end;
    };

    const now = new Date();
    const todayStart = startOfDay(now);

    switch (dateFilterOption) {
      case 'today':
        return { start: todayStart, end: endOfDay(now) };
      case 'yesterday': {
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        return { start: yesterdayStart, end: endOfDay(new Date(yesterdayStart)) };
      }
      case 'last7': {
        const start = new Date(todayStart);
        start.setDate(start.getDate() - 6);
        return { start, end: endOfDay(now) };
      }
      case 'last30': {
        const start = new Date(todayStart);
        start.setDate(start.getDate() - 29);
        return { start, end: endOfDay(now) };
      }
      case 'custom': {
        const hasStart = Boolean(customDateRange.start);
        const hasEnd = Boolean(customDateRange.end);
        if (!hasStart && !hasEnd) {
          return undefined;
        }

        const customStart = hasStart
          ? startOfDay(new Date(customDateRange.start))
          : undefined;
        const customEnd = hasEnd
          ? endOfDay(new Date(customDateRange.end))
          : undefined;
        return { start: customStart, end: customEnd };
      }
      case 'all':
      default:
        return undefined;
    }
  }, [customDateRange.end, customDateRange.start, dateFilterOption]);

  const filteredItems = useMemo(() => {
    const baseItems = !searchQuery.trim()
      ? filteredByCollection
      : filteredByCollection.filter((item) =>
          item.query.toLowerCase().includes(searchQuery.toLowerCase()),
        );

    if (!dateRange) {
      return baseItems;
    }

    return baseItems.filter((item) => {
      const itemDate = new Date(item.timestamp);
      if (dateRange.start && itemDate < dateRange.start) {
        return false;
      }
      if (dateRange.end && itemDate > dateRange.end) {
        return false;
      }
      return true;
    });
  }, [filteredByCollection, searchQuery, dateRange]);

  useEffect(() => {
    // Auto-select the first item only when there is no selection yet
    if (selectedItem === null && filteredItems.length > 0) {
      setSelectedItem(filteredItems[0]);
    }
  }, [filteredItems, selectedItem]);

  const confirmClearHistory = () =>
    window.confirm('Are you sure you want to clear all search history?');

  const handleSearch = (query: string) => {
    navigate(buildSearchResultsPath(query));
  };

  const handleDelete = (timestamp: number) => {
    removeFromSearchHistory(timestamp);
  };

  const activeTitle = activeCollection === 'bookmarks'
    ? 'Bookmarks'
    : activeBucket === ALL_HISTORY
      ? 'History'
      : activeBucket;

  return (
    <div className={styles.library}>
      <header className={styles.toolbar}>
        <div className={styles.toolbarButtons}>
          <div className={styles.dateFilter}>
            <label className={styles.filterLabel}>
              Date range
              <select
                value={dateFilterOption}
                onChange={(event) => {
                  const value = event.target.value as DateFilterOption;
                  setDateFilterOption(value);
                  if (value !== 'custom') {
                    setCustomDateRange({ start: '', end: '' });
                  }
                }}
                className={styles.dateSelect}
              >
                <option value="all">All dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="custom">Custom range</option>
              </select>
            </label>
            {dateFilterOption === 'custom' && (
              <div className={styles.customDateInputs}>
                <label className={styles.dateInputLabel}>
                  From
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(event) =>
                      setCustomDateRange((prev) => ({ ...prev, start: event.target.value }))
                    }
                    className={styles.dateInput}
                  />
                </label>
                <label className={styles.dateInputLabel}>
                  To
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(event) =>
                      setCustomDateRange((prev) => ({ ...prev, end: event.target.value }))
                    }
                    className={styles.dateInput}
                  />
                </label>
                <button
                  type="button"
                  className={styles.clearCustomRange}
                  onClick={() => setCustomDateRange({ start: '', end: '' })}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <span className={styles.toolbarDivider} />
          <button
            className={`${styles.toolbarButton} ${styles.clearHistoryButton}`}
            type="button"
            disabled={activeCollection !== 'history' || searchHistory.length === 0}
            onClick={() => {
              if (activeCollection === 'history' && searchHistory.length && confirmClearHistory()) {
                clearSearchHistory();
              }
            }}
          >
            Clear all
          </button>
        </div>
        <div className={styles.toolbarRight}>
          <div className={styles.searchWrapper}>
            <MagnifyingGlassIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search History"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
      </header>

      <div className={styles.libraryBody}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <p className={styles.sidebarHeader}>History</p>
            <div className={styles.sidebarList}>
              {historyBuckets.map((bucket) => (
                <button
                  key={bucket.label}
                  className={`${styles.sidebarItem} ${
                    activeCollection === 'history' && activeBucket === bucket.label
                      ? styles.sidebarItemActive
                      : ''
                  }`}
                  onClick={() => {
                    setActiveCollection('history');
                    setActiveBucket(bucket.label);
                  }}
                  type="button"
                >
                  <span>{bucket.label}</span>
                  <span className={styles.sidebarCount}>{bucket.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <p className={styles.sidebarHeader}>All bookmarks</p>
            <button
              className={`${styles.sidebarItem} ${
                activeCollection === 'bookmarks' ? styles.sidebarItemActive : ''
              }`}
              onClick={() => {
                setActiveCollection('bookmarks');
                setActiveBucket(ALL_HISTORY);
              }}
              type="button"
            >
              <span>Bookmarks</span>
              <span className={styles.sidebarCount}>{bookmarks.length}</span>
            </button>
          </div>
        </aside>

        <main className={styles.contentArea}>
          <div style={{ marginBottom: '0.5rem' }}>
            <Eyebrow tone="default" dot>SEARCH HISTORY</Eyebrow>
          </div>
          <PageHeading
            accentWord="History"
            accentTone="orange"
            size="h3"
            align="left"
            level="h2"
            className={styles.sectionTitle}
          >
            {activeTitle}
          </PageHeading>

          <div className={styles.tableCard}>
            <div className={styles.tableHead}>
              <span>Name</span>
              <span>Tags</span>
              <span>Location</span>
            </div>

            <div className={styles.tableBody}>
              {filteredItems.length === 0 ? (
                <div className={styles.emptyState}>
                  {searchQuery
                    ? 'No entries match your search.'
                    : activeCollection === 'bookmarks'
                      ? 'No bookmarks saved yet.'
                      : 'No history available for this view.'}
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isSelected = selectedItem?.timestamp === item.timestamp;
                  const url = buildQueryUrl(
                    item.query,
                    searchBasePath === '/search' ? '/search/results' : searchBasePath,
                  );

                  return (
                    <div
                      key={item.timestamp}
                      className={`${styles.tableRow} ${isSelected ? styles.selectedRow : ''}`}
                      onClick={() => setSelectedItem(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          setSelectedItem(item);
                        }
                      }}
                    >
                      <div className={styles.nameCell}>
                        <span className={styles.siteIcon}>
                          {item.query.charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <p className={styles.primaryText}>{item.query}</p>
                          <p className={styles.secondaryText}>{formatTimestamp(item.timestamp)}</p>
                        </div>
                      </div>

                      <div className={styles.tagsCell}>
                        {item.isBookmarked && (
                          <span className={styles.tag}>Bookmarked</span>
                        )}
                      </div>

                      <div className={styles.locationCell}>
                        <a
                          href={url}
                          className={styles.linkText}
                          onClick={(event) => {
                            event.preventDefault();
                            handleSearch(item.query);
                          }}
                        >
                          {url}
                        </a>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSearch(item.query);
                            }}
                            aria-label={`Open ${item.query} search`}
                          >
                            <ArrowTopRightOnSquareIcon />
                          </button>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleBookmark(item.timestamp);
                            }}
                            aria-label={item.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                          >
                            {item.isBookmarked ? (
                              <BookmarkSolidIcon />
                            ) : (
                              <BookmarkOutlineIcon />
                            )}
                          </button>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDelete(item.timestamp);
                            }}
                            aria-label={`Delete ${item.query} from history`}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className={styles.detailsPane}>
            <div className={styles.detailField}>
              <label htmlFor="selected-name" className={styles.detailLabel}>
                Name
              </label>
              <input
                id="selected-name"
                className={styles.detailValue}
                value={selectedItem?.query ?? ''}
                readOnly
                placeholder="Select an entry"
              />
            </div>
            <div className={styles.detailField}>
              <label htmlFor="selected-url" className={styles.detailLabel}>
                URL
              </label>
              <input
                id="selected-url"
                className={styles.detailValue}
                value={selectedItem
                  ? buildQueryUrl(
                      selectedItem.query,
                      searchBasePath === '/search' ? '/search/results' : searchBasePath,
                    )
                  : ''}
                readOnly
                placeholder="Select an entry"
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export { SearchHistoryPage };
